import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useClientPagination } from '@/hooks/useClientPagination';
import { type ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, Sparkles, TrendingUp } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { KPICard } from '@/components/charts/KPICard';
import { DataView } from '@/components/data/DataView';
import { SnapshotMenu } from '@/components/system/SnapshotMenu';
import { TaxonomyRail } from '@/components/inventory/TaxonomyRail';
import { ForecastChart } from '@/components/products/ForecastChart';
import {
  useInventoryTaxonomy,
  useInventoryForecast,
  useProductForecast,
  useSearchZeroResults,
  type ForecastRow,
  type ForecastUrgency,
  type SearchZeroResultsRow,
} from '@/api/bi';
import { useProductScope } from '@/pages/products/useProductScope';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

const HORIZONS = [7, 14, 30] as const;

const URG_TONE: Record<ForecastUrgency, 'danger' | 'warning' | 'success'> = {
  out: 'danger',
  critical: 'danger',
  low: 'warning',
  ok: 'success',
};

export function ProductForecastPage() {
  const scope = useProductScope();
  const taxonomy = useInventoryTaxonomy();
  const [horizon, setHorizon] = useState<(typeof HORIZONS)[number]>(14);

  const forecast = useProductForecast({ horizon, lookback: 90, ...scope.taxonomy });

  const atRisk = useInventoryForecast({
    storeId: scope.storeId,
    storeIds: scope.storeIds,
    vendorIds: scope.vendorIds,
    brandIds: scope.brandIds,
    categoryGroupIds: scope.categoryGroupIds,
    categoryIds: scope.categoryIds,
    subcategoryIds: scope.subcategoryIds,
    q: scope.productQuery,
    horizon,
    limit: 100,
  });

  const zeroResults = useSearchZeroResults({
    start: scope.iso.start,
    end: scope.iso.end,
    storeId: scope.storeId,
  });

  const atRiskRows = atRisk.data?.rows ?? [];
  const zeroResultRows = zeroResults.data?.rows ?? [];
  const atRiskPagination = useClientPagination(10, `${horizon}-${atRiskRows.length}`);
  const zeroPagination = useClientPagination(10, `${scope.iso.start}-${zeroResultRows.length}`);

  const summary = useMemo(() => {
    const fc = forecast.data?.forecast ?? [];
    const projectedUnits = fc.reduce((s, p) => s + p.yhat, 0);
    const projectedGmv = fc.reduce((s, p) => s + (p.gmv_yhat ?? 0), 0);
    const projectedOrders = fc.reduce((s, p) => s + (p.orders_yhat ?? 0), 0);
    const avgDailyUnits = fc.length ? projectedUnits / fc.length : 0;
    const avgDailyGmv = fc.length ? projectedGmv / fc.length : 0;
    const avgDailyOrders = fc.length ? projectedOrders / fc.length : 0;
    const hist = forecast.data?.history ?? [];
    const trailing = hist.slice(-horizon);
    const trailingUnitsAvg = trailing.length
      ? trailing.reduce((s, h) => s + h.units, 0) / trailing.length
      : 0;
    const trailingGmvAvg = trailing.length
      ? trailing.reduce((s, h) => s + (h.gmv ?? 0), 0) / trailing.length
      : 0;
    const trailingOrdersAvg = trailing.length
      ? trailing.reduce((s, h) => s + (h.orders ?? 0), 0) / trailing.length
      : 0;
    return {
      projectedUnits,
      projectedGmv,
      projectedOrders,
      avgDailyUnits,
      avgDailyGmv,
      avgDailyOrders,
      trailingUnitsAvg,
      trailingGmvAvg,
      trailingOrdersAvg,
    };
  }, [forecast.data, horizon]);

  const urgencyColumns = useMemo<ColumnDef<ForecastRow, unknown>[]>(
    () => [
      { id: 'product', header: 'Product', cell: ({ row }) => (
        <div className="min-w-0">
          <Link
            to={`/products/${row.original.product_id}`}
            className="truncate text-sm font-medium text-primary hover:underline"
          >
            {row.original.product_name}
          </Link>
          <div className="text-[11px] text-muted-foreground">{row.original.brand ?? '—'} · {row.original.store_name ?? '—'}</div>
        </div>
      ) },
      { id: 'urgency', header: 'Status', cell: ({ row }) => (
        <Badge tone={URG_TONE[row.original.urgency]} className="gap-1">
          {row.original.urgency === 'out' || row.original.urgency === 'critical' ? <AlertTriangle className="h-3 w-3" /> : null}
          {row.original.urgency}
        </Badge>
      ) },
      { id: 'available', header: () => <div className="text-right">On hand</div>, cell: ({ row }) => (
        <div className="kpi-num text-right text-sm">{fmt.number(row.original.available)}</div>
      ) },
      { id: 'cover', header: () => <div className="text-right">Cover (d)</div>, cell: ({ row }) => (
        <div className="kpi-num text-right text-sm">{row.original.cover_days != null ? row.original.cover_days.toFixed(1) : '—'}</div>
      ) },
      { id: 'demand', header: () => <div className="text-right">Daily demand</div>, cell: ({ row }) => (
        <div className="kpi-num text-right text-sm">{row.original.daily_demand.toFixed(2)}</div>
      ) },
      { id: 'lead', header: () => <div className="text-right">Lead (d)</div>, cell: ({ row }) => (
        <div className="kpi-num text-right text-sm">{row.original.lead_time_days ?? '—'}</div>
      ) },
      { id: 'suggested', header: () => <div className="text-right">Suggested qty</div>, cell: ({ row }) => (
        <div className="kpi-num text-right text-sm">
          {row.original.suggested_quantity != null ? fmt.number(row.original.suggested_quantity) : '—'}
        </div>
      ) },
    ],
    [],
  );

  const unmetColumns = useMemo<ColumnDef<SearchZeroResultsRow, unknown>[]>(
    () => [
      { id: 'term', header: 'Search term', cell: ({ row }) => <span className="text-sm font-medium">{row.original.term}</span> },
      { id: 'count', header: () => <div className="text-right">Searches</div>, cell: ({ row }) => (
        <div className="kpi-num text-right text-sm">{fmt.number(row.original.count)}</div>
      ) },
      { id: 'users', header: () => <div className="text-right">Users</div>, cell: ({ row }) => (
        <div className="kpi-num text-right text-sm">{fmt.number(row.original.unique_users)}</div>
      ) },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        eyebrow="Merchandising"
        title="Demand forecast"
        description="Baseline daily-demand projection with an 80% confidence band, days-of-cover risk and unmet demand for the active scope."
        actions={
          <div className="flex items-center gap-2">
            <Badge tone="info" className="gap-1"><Sparkles className="h-3 w-3" /> Baseline</Badge>
            <SnapshotMenu filename="products-forecast" />
          </div>
        }
      />

      <TaxonomyRail
        taxonomy={taxonomy.data}
        taxonomyLoading={taxonomy.isLoading}
        vendorIds={scope.vendorIds}
        onVendorIds={scope.setVendorIds}
        brandIds={scope.brandIds}
        onBrandIds={scope.setBrandIds}
        categoryGroupIds={scope.categoryGroupIds}
        onCategoryGroupIds={scope.setCategoryGroupIds}
        categoryIds={scope.categoryIds}
        onCategoryIds={scope.setCategoryIds}
        subcategoryIds={scope.subcategoryIds}
        onSubcategoryIds={scope.setSubcategoryIds}
        q={scope.productQuery}
        onQ={scope.setProductQuery}
        onClear={scope.hasSelection ? scope.clear : undefined}
        trailing={
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium text-muted-foreground">Horizon</label>
            <div className="inline-flex items-center gap-0.5 rounded-md border border-border bg-surface p-0.5">
              {HORIZONS.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setHorizon(h)}
                  className={cn(
                    'press inline-flex h-7 items-center rounded-sm px-2.5 text-[11px] font-medium focus-ring',
                    horizon === h ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  {h}d
                </button>
              ))}
            </div>
          </div>
        }
      />

      <div className="grid gap-4 stagger sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <KPICard
          label={`Projected units (${horizon}d)`}
          value={fmt.compact(summary.projectedUnits)}
          loading={forecast.isLoading}
          hint="Sum of unit forecast estimates over the horizon."
        />
        <KPICard
          label={`Projected GMV (${horizon}d)`}
          value={fmt.npr(summary.projectedGmv)}
          loading={forecast.isLoading}
          hint="Sum of GMV forecast estimates over the horizon."
        />
        <KPICard
          label={`Projected orders (${horizon}d)`}
          value={fmt.compact(summary.projectedOrders)}
          loading={forecast.isLoading}
          hint="Sum of order-count forecast estimates over the horizon."
        />
        <KPICard
          label="Avg daily forecast"
          value={fmt.number(Math.round(summary.avgDailyUnits))}
          loading={forecast.isLoading}
          hint="Mean projected units per day."
        />
        <KPICard
          label="Trailing daily GMV"
          value={fmt.npr(Math.round(summary.trailingGmvAvg))}
          loading={forecast.isLoading}
          hint={`Mean daily GMV over the last ${horizon} days.`}
        />
        <KPICard
          label="SKUs at risk"
          value={fmt.number(atRisk.data?.totals.at_risk ?? 0)}
          goodWhenUp={false}
          loading={atRisk.isLoading}
          hint="Out / critical / low days-of-cover in scope."
        />
      </div>

      <div className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle>Daily demand & forecast</CardTitle>
            <Badge tone="neutral" className="gap-1"><TrendingUp className="h-3 w-3" /> {forecast.data?.method ?? 'baseline'}</Badge>
          </CardHeader>
          <CardBody>
            {forecast.isLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : forecast.isError ? (
              <ErrorState onRetry={() => void forecast.refetch()} />
            ) : (
              <ForecastChart data={forecast.data} />
            )}
          </CardBody>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <DataView<ForecastRow>
          title="Most at-risk SKUs"
          description="Lowest days-of-cover first."
          data={atRiskRows}
          columns={urgencyColumns}
          isLoading={atRisk.isLoading}
          isError={atRisk.isError}
          onRetry={() => void atRisk.refetch()}
          clientPagination
          pagination={{ ...atRiskPagination, total: atRiskRows.length }}
          emptyTitle="No stock-risk rows in scope"
          density="compact"
        />
        <DataView<SearchZeroResultsRow>
          title="Unmet demand"
          description="Top zero-result searches — product customers wanted but couldn't find."
          data={zeroResultRows}
          columns={unmetColumns}
          isLoading={zeroResults.isLoading}
          isError={zeroResults.isError}
          onRetry={() => void zeroResults.refetch()}
          clientPagination
          pagination={{ ...zeroPagination, total: zeroResultRows.length }}
          emptyTitle="No zero-result searches in range"
          density="compact"
        />
      </div>
    </>
  );
}
